import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TabScroller } from '@/components/ui/tab-scroller';
import type { BreadcrumbSegment } from '@/contexts/PageChromeContext';
import { usePageChrome, useRegisterPageChrome } from '@/contexts/PageChromeContext';
import { cn } from '@/lib/utils';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type Ref } from 'react';
import { Link } from 'react-router-dom';

export interface RouteTabItem {
  id: string;
  to: string;
  label: string;
}

/**
 * The single pillar-level tab strip: route-linked tabs in one consistent
 * (Linear-style) treatment. Used by every pillar so Trade, Banking, etc. look
 * identical. For in-page (non-route) tabs use `SegmentedTabs`.
 */
export function RouteTabs({
  items,
  activeId,
  ariaLabel,
  onPrefetch,
  scrollCollapse,
}: {
  items: RouteTabItem[];
  activeId: string;
  ariaLabel: string;
  onPrefetch?: (to: string) => void;
  /** When set, registers a breadcrumb dropdown when the tab row scrolls off-screen. */
  scrollCollapse?: { chromeKey: string };
}) {
  const { scrollRef } = usePageChrome();
  const tabRowRef = useRef<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!scrollCollapse) return;
    const target = tabRowRef.current;
    if (!target) return;
    const root = scrollRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setCollapsed(!entry.isIntersecting);
      },
      {
        root: root ?? null,
        threshold: 0,
      }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [scrollCollapse, scrollRef]);

  const activeItem = items.find((item) => item.id === activeId);
  const activeLabel = activeItem?.label ?? activeId;

  const breadcrumbSegment: BreadcrumbSegment = useMemo(
    () => ({
      dropdownLabel: activeLabel,
      trigger: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type='button'
              className='text-foreground hover:bg-accent focus-visible:ring-ring inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none'
              aria-label={ariaLabel}
            >
              <span className='truncate'>{activeLabel}</span>
              <ChevronDown className='size-3.5 opacity-70' aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='start' className='min-w-[10rem]'>
            {items.map((item) => (
              <DropdownMenuItem key={item.id} asChild>
                <Link
                  to={item.to}
                  className={cn(
                    'flex items-center justify-between gap-2',
                    item.id === activeId && 'text-foreground'
                  )}
                >
                  <span className='truncate'>{item.label}</span>
                  {item.id === activeId && <Check className='size-3.5 opacity-70' />}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }),
    [activeId, activeLabel, ariaLabel, items]
  );

  const segments = useMemo(
    () => (scrollCollapse && collapsed ? [breadcrumbSegment] : []),
    [breadcrumbSegment, collapsed, scrollCollapse]
  );

  useRegisterPageChrome({
    key: scrollCollapse?.chromeKey ?? `${ariaLabel}:route-tabs`,
    ...(scrollCollapse ? { segments } : {}),
  });

  return (
    <TabScroller
      ref={tabRowRef as Ref<HTMLElement>}
      ariaLabel={ariaLabel}
      className='border-fin-gray-800 border-b pb-px'
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <Link
            key={item.id}
            to={item.to}
            onMouseEnter={onPrefetch ? () => onPrefetch(item.to) : undefined}
            onTouchStart={onPrefetch ? () => onPrefetch(item.to) : undefined}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'shrink-0 rounded-t-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors',
              active
                ? 'border-fin-accent text-fin-white bg-fin-gray-900/40 border-b-2'
                : 'text-fin-gray-400 hover:text-fin-gray-200'
            )}
          >
            {item.label}
          </Link>
        );
      })}
    </TabScroller>
  );
}
